import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import api from '../utils/api';

/**
 * User profile data from the backend.
 */
export interface UserProfile {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string | null;
  role?: string;
  subscriptionPlan?: string;
  aliId?: string;
  businessName?: string;
  businessType?: string;
  watermarkEnabled?: boolean;
  requirePasswordForDelete?: boolean;
}

export interface UseUserProfileReturn {
  /** Full user profile data */
  profile: UserProfile | null;
  /** User's display name for artist attribution */
  displayName: string | null;
  /** Whether the profile is currently being loaded */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to update the user's display name */
  updateDisplayName: (newDisplayName: string) => Promise<void>;
  /** Function to update the user's first and last name */
  updateFullName: (firstName: string, lastName: string) => Promise<void>;
  /** Function to update watermark preference */
  updateWatermarkEnabled: (enabled: boolean) => Promise<void>;
  /** Function to update require-password-for-delete preference */
  updateRequirePasswordForDelete: (enabled: boolean) => Promise<void>;
  /** Function to refetch the profile */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage the current user's profile.
 *
 * Provides access to the user's profile data including displayName,
 * and a function to update the display name.
 *
 * @example
 * const { displayName, updateDisplayName, loading, error } = useUserProfile();
 *
 * // Display the artist name
 * const artistName = getArtistDisplayName(displayName);
 *
 * // Update the display name
 * await updateDisplayName('New Artist Name');
 */
export const useUserProfile = (): UseUserProfileReturn => {
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/user/profile');
      setProfile(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const updateDisplayName = async (newDisplayName: string): Promise<void> => {
    try {
      const response = await api.put('/api/user/profile', {
        displayName: newDisplayName,
      });
      setProfile(response.data);
    } catch (err) {
      // Re-throw to allow caller to handle
      throw err instanceof Error ? err : new Error('Failed to update display name');
    }
  };

  const updateFullName = async (firstName: string, lastName: string): Promise<void> => {
    try {
      const response = await api.put('/api/user/profile', {
        firstName,
        lastName,
      });
      setProfile(response.data);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update name');
    }
  };

  const updateWatermarkEnabled = async (enabled: boolean): Promise<void> => {
    try {
      const response = await api.put('/api/user/profile', {
        watermarkEnabled: enabled,
      });
      setProfile(response.data);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update watermark preference');
    }
  };

  const updateRequirePasswordForDelete = async (enabled: boolean): Promise<void> => {
    try {
      const response = await api.put('/api/user/profile', {
        requirePasswordForDelete: enabled,
      });
      setProfile(response.data);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update delete confirmation preference');
    }
  };

  const refetch = async () => {
    await fetchProfile();
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    } else {
      setProfile(null);
      setError(null);
    }
  }, [isAuthenticated, fetchProfile]);

  // Resolve displayName: explicit displayName → firstName+lastName → null
  const resolvedDisplayName = (() => {
    if (profile?.displayName?.trim()) return profile.displayName.trim();
    const first = profile?.firstName?.trim() ?? '';
    const last = profile?.lastName?.trim() ?? '';
    const fullName = `${first} ${last}`.trim();
    return fullName || null;
  })();

  return {
    profile,
    displayName: resolvedDisplayName,
    loading,
    error,
    updateDisplayName,
    updateFullName,
    updateWatermarkEnabled,
    updateRequirePasswordForDelete,
    refetch,
  };
};
